define([
    '../slide',
    '../../model/slide-activation-tracker'
], function (SlideComponent, slideActivation) {
    'use strict';

    function getImgScale (imgEl) {
        return Math.max(
            imgEl.width / imgEl.naturalWidth,
            imgEl.height / imgEl.naturalHeight
        );
    }

    /**
     * @class MaterialsZoomGroupComponent
     * @extends {SlideComponent}
     * @inheritDoc
     * @property {gsap.core.Timeline} childrenTimeline
     */
    return SlideComponent.extend({
        defaults: {
            childrenTimeline: null,
            imageTimeline: null,
            activeSlide: null,
            imgEl: null,
            widthCutoff: 0,
            heightCutoff: 0,
            imgScale: 1,
            landscapeOrientation: true
        },

        initObservable: function () {
            this._super();
            this.observe(['landscapeOrientation']);
            return this;
        },

        /**
         * @param {Element} groupEl cross-fade slide grouping to initialize
         */
        attachElement: function (groupEl) {
            this._super(groupEl);

            /** @type HTMLImageElement */
            this.imgEl = groupEl.querySelector('.media-bg');
            this.imgScale = getImgScale(this.imgEl);

            this.widthCutoff = (this.imgEl.naturalWidth * this.imgScale - this.imgEl.width) / 2 ;
            this.heightCutoff = (this.imgEl.naturalHeight * this.imgScale - this.imgEl.height) / 2;
            this.imgEl.style.setProperty('--media-bg-position', `-${this.widthCutoff}px -${this.heightCutoff}px`);

            const children = [...groupEl.querySelectorAll('.materials-zoom')];
            const menuHeightAccommodation = getComputedStyle(groupEl).getPropertyValue('--ft-menu-height-adjust');

            /* eslint-disable func-style */
            const startFn = () => `top-=${menuHeightAccommodation} top`;
            const endFn = () => `+=${children.length * 100}%`;
            const onEnter = () => this.imgEl.style.willChange = 'object-position, scale';
            const onLeave = () =>  this.imgEl.style.removeProperty('will-change');
            /* eslint-enable func-style */

            this.ScrollTrigger.create({
                trigger: groupEl,
                pin: groupEl,
                start: startFn,
                end: endFn,
                onEnter: onEnter,
                onEnterBack: onEnter,
                onLeave: onLeave,
                onLeaveBack: onLeave
            });

            const timelineVars = {
                defaults: {ease: 'none'},
                scrollTrigger: {
                    trigger: groupEl,
                    scrub: 1,
                    start: startFn,
                    end: endFn
                }
            };

            /** @type Timeline */
            this.childrenTimeline = this.gsap.timeline(Object.assign({}, timelineVars, {
                onUpdateParams: [this],
                /**
                 * Handle the update (EVERY FRAME OF IT) of the timeline to potentially activate a "new slide"
                 * Note the use of a `function` instead of `() =>`, the former can bind to a new this, the latter inherits from closure
                 *
                 * @this Timeline
                 * @param {MaterialsZoomGroupComponent} group current group
                 */
                onUpdate: function (group) {
                    const currentLabel = group.childrenTimeline.currentLabel();
                    const scrollTrigger = this.scrollTrigger;
                    if (this.activeSlide !== currentLabel && scrollTrigger.isActive) {
                        this.activeSlide = currentLabel;
                        slideActivation.notifyActivation(currentLabel, scrollTrigger.direction);
                    }
                }
            }));

            /** @type Timeline */
            this.imageTimeline = this.gsap.timeline(timelineVars);

            this.gsap.set(children.slice(1), {autoAlpha: 0});
        },

        /**
         * @param {Element} childEl a child element of the group
         */
        attachChild: function (childEl) {
            // eslint-disable-next-line func-style
            const handleOrientationChange = mql => this.landscapeOrientation(mql.matches);
            const mq = window.matchMedia('(orientation: landscape)');
            handleOrientationChange(mq);
            mq.addEventListener('change', handleOrientationChange);

            const config = JSON.parse(childEl.getAttribute('data-config'));
            this.childrenTimeline.addPause('+=0.9');
            this.childrenTimeline.to(childEl, {
                autoAlpha: 1,
                duration: 0.1
            });

            this.childrenTimeline.addLabel(config.index);

            const viewbox = config.viewbox;
            this.childrenTimeline.addPause(`+=${viewbox.duration ? viewbox.duration : 2}`);

            const targetScale = Math.max(
                1,
                Math.min(
                    (this.imgEl.naturalWidth - this.widthCutoff * 2 / this.imgScale) / viewbox[this.landscapeOrientation() ? 'width' : 'height'],
                    (this.imgEl.naturalHeight - this.heightCutoff * 2 / this.imgScale) / viewbox[this.landscapeOrientation() ? 'height' : 'width']
                )
            );
            const targetX = this.imgEl.width / 2 - viewbox[this.landscapeOrientation() ? 'left' : 'top'] * this.imgScale;
            const targetY = this.imgEl.height / 2 - (this.landscapeOrientation() ? viewbox.top : this.imgEl.naturalHeight - viewbox.left) * this.imgScale;
            this.imageTimeline.to(
                this.imgEl,
                {
                    ease: 'power2.inOut',
                    duration: viewbox.duration ? viewbox.duration : 2,
                    objectPosition: `${targetX}px ${targetY}px`,
                    scale: () => targetScale
                }
            );
            this.imageTimeline.addPause("+=1");
        }
    });
});
